import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { Plus, X, LibraryBig, Check, AlertTriangle } from 'lucide-react';
import agtEduService from '../services/agtEduService';

/**
 * Component for uploading individual files without associating them with a subject
 */
const IndividualFileUploader = ({ colors, onUploadComplete }) => {
  // Reference to the hidden file input
  const fileInputRef = useRef(null);
  
  // Component states
  const [open, setOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState([]);
  const [error, setError] = useState(null);

  // Event handlers
  const handleOpen = () => {
    setOpen(true);
    setSelectedFiles([]);
    setUploadResults([]);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    // If there are upload results and the upload completed successfully, notify the parent component
    if (uploadResults.length > 0 && !uploading) {
      onUploadComplete && onUploadComplete(uploadResults);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    // Reset states
    setUploadProgress(0);
    setUploadResults([]);
    setError(null);
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      
      // Create FormData for file upload
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      // Progress tracking function
      const onProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadProgress(percentCompleted);
      };

      // Call the service to upload individual files
      const response = await agtEduService.uploadIndividualFiles(formData, onProgress);
      
      // Set results
      setUploadResults(response.data.files || []);
      
    } catch (error) {
      console.error('Error uploading files:', error);
      setError('Could not upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Determine button text based on state
  const getButtonText = () => {
    if (uploading) return "Uploading...";
    if (uploadResults.length > 0) return "Done";
    return "Upload";
  };

  // Determine button color based on state
  const getButtonColor = () => {
    if (uploadResults.length > 0) return colors.success;
    return colors.accent;
  };

  return (
    <>
      {/* Initial button that opens the modal */}
      <IconButton
        onClick={handleOpen}
        sx={{
          width: 36,
          height: 36,
          color: colors.textSecondary,
          border: '1px solid',
          borderColor: colors.borderColor,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: colors.cardBackground
          },
          flexShrink: 0
        }}
      >
        <Plus size={16} />
      </IconButton>

      {/* File upload modal */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: colors.background
          }
        }}
      >
        <DialogTitle sx={{ color: colors.textDefault }}>
          Upload files for analysis
        </DialogTitle>
        
        <DialogContent>
          {/* File selection area */}
          <Box
            sx={{
              border: '2px dashed',
              borderColor: colors.borderColor,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              mb: 2,
              backgroundColor: colors.cardBackground
            }}
            onClick={handleFileInputClick}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              multiple
              accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.ppt,.pptx"
            />
            
            <Plus size={40} style={{ color: colors.textSecondary, margin: '0 auto 16px' }} />
            
            <Typography variant="body1" sx={{ color: colors.textDefault, mb: 1 }}>
              Click to select files or drag them here
            </Typography>
            
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Supports PDF, Word, Excel, PowerPoint, Text, and CSV
            </Typography>
          </Box>

          {/* List of selected files */}
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: colors.textDefault }}>
                Selected files ({selectedFiles.length})
              </Typography>
              
              <List sx={{ bgcolor: colors.cardBackground, borderRadius: 1 }}>
                {selectedFiles.map((file, index) => (
                  <ListItem 
                    key={index}
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        onClick={() => handleRemoveFile(index)}
                        disabled={uploading}
                        sx={{ color: colors.textSecondary }}
                      >
                        <X size={16} />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <LibraryBig size={20} style={{ color: colors.accent }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={file.name} 
                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      primaryTypographyProps={{ color: colors.textDefault }}
                      secondaryTypographyProps={{ color: colors.textSecondary }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Progress bar */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                Uploading files ({uploadProgress}%)
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ 
                  borderRadius: 1,
                  backgroundColor: `${colors.accent}30`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: colors.accent
                  }
                }}
              />
            </Box>
          )}

          {/* Upload results */}
          {uploadResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textDefault, mb: 1 }}>
                Files uploaded successfully ({uploadResults.length})
              </Typography>
              
              <List sx={{ bgcolor: colors.cardBackground, borderRadius: 1 }}>
                {uploadResults.map((result, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Check size={20} style={{ color: colors.success }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={result.originalName}
                      secondary={`Ready for analysis`}
                      primaryTypographyProps={{ color: colors.textDefault }}
                      secondaryTypographyProps={{ color: colors.textSecondary }}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Typography variant="body2" sx={{ mt: 2, color: colors.success }}>
                Files are available for analysis in the chat.
              </Typography>
            </Box>
          )}

          {/* Error message */}
          {error && (
            <Box 
              sx={{ 
                mt: 2, 
                p: 2, 
                borderRadius: 1, 
                backgroundColor: `${colors.error}20`,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AlertTriangle size={16} style={{ color: colors.error }} />
              <Typography variant="body2" sx={{ color: colors.error }}>
                {error}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: colors.borderColor,
              color: colors.textSecondary,
              '&:hover': {
                borderColor: colors.textDefault,
                backgroundColor: 'transparent'
              }
            }}
          >
            {uploadResults.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading || uploadResults.length > 0}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: getButtonColor(),
              color: '#ffffff',
              '&:hover': {
                backgroundColor: getButtonColor()
              }
            }}
          >
            {getButtonText()}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IndividualFileUploader;